import { apiFetch } from './api-client';
import { useEffect, useRef } from 'react';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { useChatOverlayOpen } from './use-chat-overlay';
import { API_BASE } from './api-base';
import { KEEP_ALIVE_MS, shouldReport } from './presence-decision';

export { ACTIVITY_IDLE_MS, KEEP_ALIVE_MS, shouldReport } from './presence-decision';
export type { PresenceInputs } from './presence-decision';

/**
 * Chat-presence reporter (web half of chat-presence → push suppression;
 * docs/chat-presence-push-suppression.md §3.3).
 *
 * Reports the user is actively viewing a conversation so the server-side push
 * gate can drop the assistant push for a conversation being watched on another
 * surface (e.g. chatting on web ⇒ the phone doesn't buzz). We report "viewing C"
 * only when a chat surface is open on C, the tab is visible, AND there's been
 * recent user activity — and stop (send `viewing:false`) the moment any of those
 * stops. The server key is TTL-backed, so a lost leave-ping self-heals.
 *
 * Mount once per app in brand-neutral chrome (both AppFrames). Brand handling is
 * entirely inside useChatOverlayOpen, so this hook stays fork-agnostic.
 */
export function usePresenceReporter(): void {
  const overlayOpen = useChatOverlayOpen();
  const { activeConversationId } = useActiveConversation();

  // Live inputs read by reconcile() (which runs from listeners/interval, not
  // renders), kept in refs so the mount effect never has to tear down.
  const propsRef = useRef({ overlayOpen, conversationId: activeConversationId });
  propsRef.current = { overlayOpen, conversationId: activeConversationId };

  const sessionIdRef = useRef<string>('');
  const lastActivityRef = useRef<number>(Date.now());
  // The conversation we currently hold a `viewing:true` presence for (null = none).
  const reportedRef = useRef<string | null>(null);

  // Stable reconcile via a ref so the sync effect and the mount effect below
  // share one implementation without re-subscribing listeners.
  const reconcileRef = useRef<(forceKeepAlive?: boolean) => void>(() => {});

  // Sync effect: when the overlay/conversation changes, re-evaluate. Old-id
  // `viewing:false` is handled inside reconcile via reportedRef (a conversation
  // switch sends false for the old id, then true for the new).
  useEffect(() => {
    reconcileRef.current();
  }, [overlayOpen, activeConversationId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!sessionIdRef.current) {
      sessionIdRef.current =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    const post = (conversationId: string, viewing: boolean) => {
      const body = JSON.stringify({ conversationId, sessionId: sessionIdRef.current, viewing });
      // keepalive lets an in-flight ping survive a navigation; presence is
      // best-effort so swallow every failure.
      apiFetch(`${API_BASE}/api/presence`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    };

    // Best-effort leave on tab hide / close — sendBeacon survives unload where a
    // fetch may be killed; fall back to keepalive fetch if unavailable.
    const leaveBeacon = (conversationId: string) => {
      const body = JSON.stringify({ conversationId, sessionId: sessionIdRef.current, viewing: false });
      const url = `${API_BASE}/api/presence`;
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
          return;
        }
      } catch {
        // fall through to fetch
      }
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    };

    const reconcile = (forceKeepAlive = false) => {
      const { overlayOpen: open, conversationId } = propsRef.current;
      const visible = document.visibilityState === 'visible';
      const desired = shouldReport({
        overlayOpen: open,
        conversationId,
        visible,
        lastActivityMs: lastActivityRef.current,
        now: Date.now(),
      });
      const reported = reportedRef.current;
      if (desired && conversationId) {
        if (reported !== conversationId) {
          if (reported) post(reported, false);
          reportedRef.current = conversationId;
          post(conversationId, true);
        } else if (forceKeepAlive) {
          post(conversationId, true);
        }
      } else if (reported) {
        post(reported, false);
        reportedRef.current = null;
      }
    };
    reconcileRef.current = reconcile;

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      // Only reconcile when we're NOT already reporting — resumes presence after
      // an idle lapse. While reporting, we just bump the timestamp; the interval
      // keep-alive handles refresh (avoids per-pointermove work).
      if (!reportedRef.current) reconcile();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (reportedRef.current) {
          leaveBeacon(reportedRef.current);
          reportedRef.current = null;
        }
      } else {
        lastActivityRef.current = Date.now();
        reconcile();
      }
    };

    const onPageHide = () => {
      if (reportedRef.current) {
        leaveBeacon(reportedRef.current);
        reportedRef.current = null;
      }
    };

    const activityEvents = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel', 'touchstart'] as const;
    activityEvents.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    const interval = window.setInterval(() => reconcile(true), KEEP_ALIVE_MS);

    // Initial evaluation (e.g. deep-linked straight into an open chat).
    reconcile();

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.clearInterval(interval);
      if (reportedRef.current) {
        post(reportedRef.current, false);
        reportedRef.current = null;
      }
    };
    // Mount-once: overlay/conversation flow through propsRef + the sync effect
    // (the API base is a module constant).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
